<template>
	<sg-modal v-model="isOpen" :title="modalTitle" :backdrop-dismiss="false">
		<div class="ion-padding">
			<div class="flex flex-col gap-4">
				<!-- Boundary Preview with Edit Button -->
				<div v-if="formData.latestBoundary">
					<boundary-preview-map
						:boundary="formData.latestBoundary"
						label="Current Boundary"
					/>
					<ion-button
						expand="block"
						fill="outline"
						class="mt-2"
						@click="openBoundaryModal"
					>
						<ion-icon slot="start" :icon="mapOutline" />
						Edit Boundary
					</ion-button>
				</div>

				<!-- Add Boundary Button (when no boundary exists) -->
				<ion-button
					v-else
					expand="block"
					fill="outline"
					@click="openBoundaryModal"
				>
					<ion-icon slot="start" :icon="mapOutline" />
					Add Boundary
				</ion-button>

				<!-- Planting Name -->
				<div>
					<sg-input
						v-model="formData.name"
						label="Planting Name*"
						type="text"
						:placeholder="
							suggestedName
								? `Default: ${suggestedName}`
								: 'Enter planting name'
						"
						:error="validationErrors.name"
						@input="markNameAsCustomized"
					/>
					<ion-button
						v-if="isNameCustomized && suggestedName"
						size="small"
						fill="clear"
						class="mt-1"
						@click="resetToSuggestedName"
					>
						<ion-icon slot="start" :icon="sparklesOutline" />
						Use suggested name
					</ion-button>
				</div>

				<!-- Ranch Select -->
				<sg-select-modal
					:single="formData.ranchId"
					:items="ranchOptions"
					label="Ranch"
					modal-title="Select Ranch"
					placeholder="Select ranch"
					@update:single="formData.ranchId = $event"
				/>

				<!-- Lot Multi-Select -->
				<sg-select-modal
					v-model:multi="selectedLotIds"
					:items="lotOptions"
					label="Lot"
					modal-title="Select Lot"
					placeholder="Select lot"
				/>

				<!-- Sublot -->
				<sg-input
					v-model="formData.sublot"
					label="Sublot"
					type="text"
					placeholder="Enter sublot (optional)"
				/>

				<!-- Crop Select -->
				<sg-select-modal
					:single="formData.cropId"
					:items="cropOptions"
					label="Crop*"
					modal-title="Select Crop"
					placeholder="Select crop"
					:error="validationErrors.cropId"
					@update:single="formData.cropId = $event"
				/>

				<!-- Acres -->
				<sg-input
					v-model.number="formData.acres"
					label="Acres*"
					type="number"
					placeholder="Enter acres"
					step="0.01"
					min="0"
					:error="validationErrors.acres"
				/>

				<!-- Wet Date -->
				<div>
					<sg-datetime-button
						label="Wet Date*"
						datetime="planting-wet-date"
						:error="validationErrors.wetDate"
					/>
					<date-modal
						v-model="wetDateModel"
						datetime-id="planting-wet-date"
						title="Choose Wet Date"
						:min="DATE_RANGE.min"
						:max="DATE_RANGE.max"
					/>
				</div>

				<!-- Harvest Date -->
				<div class="flex flex-row gap-2">
					<div class="flex-1">
						<sg-datetime-button
							label="Harvest Date (Optional)"
							datetime="planting-harvest-date"
							placeholder="Click to select harvest date"
							:has-value="!!harvestDateModel"
							:error="validationErrors.harvestDates"
						/>
					</div>
					<ion-button
						v-if="formData.harvestDates.length > 0"
						fill="outline"
						color="danger"
						class="mt-[29px]"
						size="small"
						@click="clearHarvestDate"
					>
						Clear
					</ion-button>
				</div>
				<date-modal
					v-model="harvestDateModel"
					datetime-id="planting-harvest-date"
					title="Choose Harvest Date"
					:min="DATE_RANGE.min"
					:max="DATE_RANGE.max"
				/>
			</div>
		</div>

		<template #footer>
			<div class="flex flex-row gap-2">
				<sg-button
					color="medium"
					class="flex-1"
					:disabled="isLoading"
					@click="closeModal"
				>
					{{ $t("common.cancel") }}
				</sg-button>
				<sg-button
					class="flex-1"
					:loading="isLoading"
					:disabled="isLoading"
					@click="submit"
				>
					{{ $t("common.save") }}
				</sg-button>
			</div>
		</template>
	</sg-modal>

	<!-- Boundary Edit Modal -->
	<boundary-edit-modal
		ref="boundaryModalRef"
		:geometry="formData.latestBoundary"
		title="Edit Planting Boundary"
		@save="handleBoundarySave"
	/>
</template>

<script setup lang="ts">
	import { ref, computed, watch } from "vue";
	import { IonButton, IonIcon } from "@ionic/vue";
	import { mapOutline, sparklesOutline } from "ionicons/icons";
	import {
		PlantingDTO,
		BoundaryType,
	} from "seedgreen-shared/dtos/PlantingDTO";
	import type { DateString } from "seedgreen-shared/types/datestring.types";
	import { getCurrentDateString } from "seedgreen-shared/types/datestring.types";
	import SgInput from "@/components/ui/SgInput.vue";
	import SgButton from "@/components/ui/SgButton.vue";
	import SgModal from "@/components/ui/modals/SgModal.vue";
	import SgSelectModal from "@/components/ui/modals/SgSelectModal.vue";
	import SgDatetimeButton from "@/components/ui/SgDatetimeButton.vue";
	import DateModal from "@/components/ui/modals/DateModal.vue";
	import BoundaryEditModal from "@/components/ui/modals/BoundaryEditModal.vue";
	import BoundaryPreviewMap from "@/components/ui/maps/BoundaryPreviewMap.vue";
	import { useRanches } from "seedgreen-shared/queries/ranch";
	import { useLots } from "seedgreen-shared/queries/lot";
	import { useCrops } from "seedgreen-shared/query/crops";
	import {
		plantingSchema,
		type PlantingFormData,
	} from "@/validation/planting.schema";
	import { structuredValidate } from "@/utils/validation";
	import type { Polygon, MultiPolygon } from "geojson";

	const props = defineProps<{
		planting?: PlantingDTO | null;
		isLoading?: boolean;
	}>();

	const isOpen = ref(false);
	const isNameCustomized = ref(false);

	const { ranches } = useRanches();
	const { lots } = useLots();
	const { crops } = useCrops();

	// Date range for date pickers
	const DATE_RANGE = {
		min: "1900-01-01",
		max: "2100-12-31",
	} as const;

	const createEmptyFormData = (): PlantingFormData => ({
		customerId: undefined,
		name: "",
		ranchId: undefined,
		lotIds: [],
		sublot: "",
		cropId: undefined,
		acres: undefined,
		wetDate: getCurrentDateString(), // Default to current date
		harvestDates: [],
		harvestDatesActual: [],
		latestBoundary: undefined,
		latestBoundaryDate: getCurrentDateString(),
		latestBoundaryType: BoundaryType.UserCreated,
		isVisible: true,
	});

	const formData = ref<PlantingFormData>(createEmptyFormData());

	const emptyValidationErrors = {
		name: "",
		cropId: "",
		wetDate: "",
		acres: "",
		harvestDates: "",
	};

	const validationErrors = ref({ ...emptyValidationErrors });

	const isEditing = computed(() => !!props.planting);

	const modalTitle = computed(() => {
		return isEditing.value ? "Edit Planting" : "Add Planting";
	});

	const ranchOptions = computed(() =>
		(ranches.value || []).map((ranch) => ({
			id: ranch.id,
			text: ranch.fullName,
		})),
	);

	const lotOptions = computed(() => {
		const allLots = lots.value || [];
		// Filter lots by selected ranch if one is selected
		const filteredLots = formData.value.ranchId
			? allLots.filter((lot) => lot.ranchId === formData.value.ranchId)
			: allLots;

		return filteredLots.map((lot) => ({
			id: lot.id,
			text: lot.fullName,
		}));
	});

	const cropOptions = computed(() =>
		(crops.value || []).map((crop) => ({
			id: crop.id,
			text: `${crop.commodity}${crop.variety ? `: ${crop.variety}` : ""}`,
		})),
	);

	// Lot IDs as Set for multi-select
	const selectedLotIds = computed({
		get: () => new Set(formData.value.lotIds),
		set: (value) => {
			formData.value.lotIds = Array.from(value);
		},
	});

	// Type casting to bridge DateString (branded type) and string
	const wetDateModel = computed({
		get: () => formData.value.wetDate as DateString | undefined,
		set: (value) => {
			formData.value.wetDate = value as string | undefined;
		},
	});

	// Converts between single value (for UI) and array (for schema)
	const harvestDateModel = computed({
		get: () => formData.value.harvestDates[0] as DateString | undefined,
		set: (value) => {
			formData.value.harvestDates = value ? [value as string] : [];
		},
	});

	/**
	 * Generates a suggested name by concatenating the first lot name with the sublot.
	 * For example: Lot "201" + Sublot "B" = "201B"
	 */
	const suggestedName = computed(() => {
		const firstLotId = formData.value.lotIds[0];
		if (!firstLotId) return "";
		const firstLot = lots.value?.find((lot) => lot.id === firstLotId);
		if (!firstLot) return "";

		const lotName = firstLot.name;
		const sublot = formData.value.sublot?.trim() || "";

		return `${lotName}${sublot}`;
	});

	/**
	 * Watch for changes to lots or sublot and auto-update the name
	 * if it hasn't been manually customized.
	 */
	watch(suggestedName, (newSuggestedName) => {
		if (!isNameCustomized.value && newSuggestedName) {
			formData.value.name = newSuggestedName;
		}
	});

	/**
	 * When ranch changes, clear any selected lots that don't belong to the new ranch.
	 */
	watch(
		() => formData.value.ranchId,
		(newRanchId) => {
			if (newRanchId && formData.value.lotIds.length > 0) {
				// Filter out lots that don't belong to the new ranch
				const validLotIds = formData.value.lotIds.filter((lotId) => {
					const lot = lots.value?.find((l) => l.id === lotId);
					return lot?.ranchId === newRanchId;
				});
				formData.value.lotIds = validLotIds;
			}
		},
	);

	/**
	 * Mark the name as customized when the user types in the field.
	 */
	function markNameAsCustomized() {
		isNameCustomized.value = true;
	}

	/**
	 * Reset the name back to the suggested default.
	 */
	function resetToSuggestedName() {
		formData.value.name = suggestedName.value;
		isNameCustomized.value = false;
	}

	/**
	 * Clear all harvest dates.
	 */
	function clearHarvestDate() {
		formData.value.harvestDates = [];
	}

	function validateForm(): boolean {
		validationErrors.value = { ...emptyValidationErrors };

		// Frontend requirement: Name is always required
		if (!formData.value.name || !formData.value.name.trim()) {
			validationErrors.value.name = "Planting name is required";
		}

		const result = structuredValidate(plantingSchema, formData.value);

		if (!result.success) {
			const errors = result.errors;
			validationErrors.value = {
				name: validationErrors.value.name || errors.name || "",
				cropId: errors.cropId || "",
				wetDate: errors.wetDate || "",
				acres: errors.acres || "",
				harvestDates: errors.harvestDates || "",
			};
			return false;
		}

		// Return true only if no errors exist
		return !Object.values(validationErrors.value).some((error) => error);
	}

	function resetForm() {
		validationErrors.value = { ...emptyValidationErrors };
		isNameCustomized.value = false;

		if (props.planting) {
			formData.value = {
				customerId: props.planting.customerId,
				name: props.planting.name ?? "",
				ranchId: props.planting.ranchId,
				lotIds: [...props.planting.lotIds],
				sublot: props.planting.sublotCurrent ?? "",
				cropId: props.planting.cropId ?? props.planting.cropIdActual,
				acres: props.planting.acresCurrent,
				wetDate: props.planting.wetDateCurrent as string | undefined,
				harvestDates: (props.planting.harvestDatesCurrent ||
					[]) as string[],
				harvestDatesActual: [],
				latestBoundary: props.planting.latestBoundary,
				latestBoundaryDate: props.planting.latestBoundaryDate as
					| string
					| undefined,
				latestBoundaryType: props.planting.latestBoundaryType,
				isVisible: true,
			};

			// After setting formData, check if the name differs from the suggested one
			// If it does, mark it as customized so we don't override it
			const existingName = props.planting.name?.trim() || "";
			if (existingName && existingName !== suggestedName.value) {
				isNameCustomized.value = true;
			}
		} else {
			formData.value = createEmptyFormData();
		}
	}

	function closeModal() {
		isOpen.value = false;
	}

	function openModal(
		existingFormData?: PlantingFormData,
		validateImmediately = false,
	) {
		if (existingFormData) {
			// Editing a queued planting
			formData.value = { ...existingFormData };

			// Reset validation errors and customization flag
			validationErrors.value = { ...emptyValidationErrors };
			isNameCustomized.value = false;

			// Check if the name differs from the suggested name
			// If it does, mark it as customized so we don't override it
			const existingName = existingFormData.name?.trim() || "";
			if (existingName && existingName !== suggestedName.value) {
				isNameCustomized.value = true;
			}
		} else {
			resetForm();
		}

		isOpen.value = true;

		// If we should validate immediately (e.g., editing an item with errors)
		// trigger validation after the modal is open
		if (validateImmediately) {
			validateForm();
		}
	}

	const boundaryModalRef = ref<InstanceType<typeof BoundaryEditModal> | null>(
		null,
	);

	function openBoundaryModal() {
		boundaryModalRef.value?.openModal();
	}

	function handleBoundarySave(geometry: Polygon | MultiPolygon | null) {
		formData.value.latestBoundary = geometry || undefined;
		formData.value.latestBoundaryType = geometry
			? BoundaryType.UserCreated
			: undefined;
		formData.value.latestBoundaryDate = getCurrentDateString() as string;
	}

	const emit = defineEmits<{
		save: [data: PlantingFormData];
	}>();

	function submit() {
		if (!validateForm()) {
			return;
		}

		// Emit save event - parent decides when to close the modal
		emit("save", formData.value);
	}

	defineExpose({
		openModal,
		closeModal,
	});
</script>
